import { Divider } from '@material-ui/core';
import React from 'react';

import { WhyOutdoorsyProps } from '@/pages/why-outdoorsy';

import GridColumn from '../static-pages/wagtail/GridColumn/GridColumn';
import RichText from '../static-pages/wagtail/RichText/RichText';
import Heading, { THeadingLevels } from '../switchback/Heading/Heading';
import ResponsiveImage from '../utility/ResponsiveImage/ResponsiveImage';
import css from './WhyOutdoorsyTemplate.module.css';

interface HeroProps {
  heading: string;
  imageUrl: string;
}

interface IBlock {
  type: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  value: any;
}

interface GridContent {
  title: string;
  url: string;
  description: string;
}

interface ISplitProps {
  left: IBlock[];
  right: IBlock[];
  className?: string;
  leftClassName?: string;
  rightClassName?: string;
  background?: string;
  HeadingLevel?: THeadingLevels;
  headingClassNames?: string;
}

const Split: React.FC<ISplitProps> = ({
  left,
  right,
  className = '',
  leftClassName = '',
  rightClassName = '',
  background = '',
  HeadingLevel = 1,
  headingClassNames = '',
}) => {
  const renderSections = (contentBlocks: IBlock[]) => {
    const sections: JSX.Element[] = [];
    contentBlocks.forEach((block, idx) => {
      switch (block.type) {
        case 'heading':
          sections.push(
            <Heading key={idx} level={HeadingLevel} className={headingClassNames}>
              {block.value}
            </Heading>,
          );
          break;
        case 'richtext':
          sections.push(<RichText key={idx} content={block.value} className="px-0 text-left" />);
          break;
      }
    });
    return sections;
  };
  return (
    <section className={background}>
      <div className={className}>
        <div className={leftClassName}>{renderSections(left)}</div>
        <div className={rightClassName}>{renderSections(right)}</div>
      </div>
    </section>
  );
};

const Hero: React.FC<HeroProps> = ({ heading, imageUrl }) => {
  return (
    <div className={css.hero}>
      <ResponsiveImage
        src={imageUrl}
        alt={'background image'}
        className={css.heroImage}
        sourceSizes={[
          'landscape280',
          'landscape375',
          'landscape768',
          'landscape1080',
          'landscape1440',
          'landscape2160',
          'landscape2880',
        ]}
      />

      <div className="z-10 w-full px-4 mx-auto sm:px-8 lg:px-12 max-w-xxl">
        <h2 className={css.heroHeading}>{heading}</h2>
      </div>
    </div>
  );
};

const WhyOutdoorsyTemplate: React.FC<WhyOutdoorsyProps> = ({
  heroHeading,
  heroImage,
  contentBlocks,
}) => {
  const displayGridContent = (gridList: GridContent[], key: number) => (
    <section key={key}>
      <div className={` grid grid-cols-2 lg:grid-cols-4 gap-16 pb-16 ${css.spacing} max-w-60`}>
        {gridList.map((list, idx) => (
          <GridColumn
            key={idx}
            containerClassName={`${idx === gridList.length - 1 ? 'col-span-2' : 'col-span-1'}`}
            title={list.title}
            description={list.description}
            imageProps={{
              height: 64,
              width: 64,
              src: list.url,
            }}
            headingLevel={5}
          />
        ))}
      </div>
    </section>
  );

  const renderSections = () => {
    const sections: JSX.Element[] = [];
    contentBlocks.forEach((block, idx) => {
      switch (block.type) {
        case 'richtext':
          if (idx === contentBlocks.length - 1) {
            sections.push(
              <RichText
                key={idx}
                content={block.value}
                className={`text-center py-16 ${css.spacing} max-w-60 md:max-w-30`}
                containerClassName="bg-gray-100"
              />,
            );
          } else {
            sections.push(
              <RichText
                key={idx}
                content={block.value}
                className={`py-16 max-w-60 ${css.spacing}`}
              />,
            );
          }
          break;
        case 'split':
          sections.push(
            <div key={idx}>
              <Split
                left={block.value.left}
                right={block.value.right}
                className={`pb-16 grid grid-cols-1 gap-16 max-w-60 box-content md:grid-cols-2 ${css.spacing}`}
                headingClassNames="h1 text-gray-800 pb-4"
              />
              <div className={`mx-auto max-w-60 ${css.spacing}`}>
                <Divider />
              </div>
            </div>,
          );
          break;
        case 'richtext-with-bg':
          if (block.value?.richtext) {
            sections.push(
              <RichText
                key={idx}
                content={block.value.richtext}
                className={`py-16 max-w-60 ${css.spacing}`}
                containerClassName="bg-gray-100"
              />,
            );
          }
          break;
        case 'content-grid':
          if (block?.value && block.value.length) {
            sections.push(displayGridContent(block.value, idx));
          }
          break;
      }
    });
    return sections;
  };
  return (
    <main>
      {heroImage && <Hero heading={heroHeading} imageUrl={heroImage} />}
      {renderSections()}
    </main>
  );
};

export default WhyOutdoorsyTemplate;
